import React, { useCallback, useEffect, useRef, useState } from "react";
import Vapi from "@vapi-ai/web";
import { vapiConfig, interviewVapiConfig, saveInterview, latestInterview, interviewHistory, getInterview, practiceAllowance } from "@/lib/api";
import { PaywallCard } from "@/pages/app/MockTestPage";
import { useAuth } from "@/contexts/AuthContext";
import Waveform from "@/components/wishire/Waveform";
import ScoreRing from "@/components/wishire/ScoreRing";
import CoachingPlan from "@/components/wishire/CoachingPlan";
import AvatarInterviewer from "@/components/wishire/AvatarInterviewer";
import { useFaceGuard, waitForFace } from "@/lib/faceGuard";
import {
  Mic,
  MicOff,
  Loader2,
  RotateCcw,
  AlertTriangle,
  CheckCircle2,
  Clock,
  ChevronRight,
  Camera,
  CameraOff,
  ShieldCheck,
  ShieldAlert,
} from "lucide-react";

const INTERVIEWER_NAME = "Naina";

const buildAssistant = (role, candidateName, modePrompt, maxSeconds) => ({
  name: "Wishire Voice Interviewer",
  // Hard session cap (6 min free / 12 min pro) — Vapi ends the call itself.
  maxDurationSeconds: maxSeconds || 720,
  firstMessage: `Hi${candidateName ? " " + candidateName : ""}, I'm ${INTERVIEWER_NAME} — I'll be taking your interview today for the ${role} role. Before we dive in: how's your day going so far?`,
  model: {
    provider: "anthropic",
    model: "claude-sonnet-4-5-20250929",
    messages: [
      {
        role: "system",
        content: `You are ${INTERVIEWER_NAME}, a warm, experienced hiring manager conducting a voice interview for a ${role} role.

CANDIDATE'S NAME: ${candidateName || "unknown"}.
- ${candidateName ? `Address the candidate ONLY as "${candidateName}". Never use any other name, even if you think you heard one in the audio — transcription can garble names. If unsure, use no name at all.` : `You don't know the candidate's name. Do NOT guess or invent one — simply speak without using a name.`}
- Use their name sparingly and naturally (2–3 times in the whole interview), the way a real interviewer would.

SOUND HUMAN, NOT SCRIPTED:
- Speak the way a friendly senior colleague talks: contractions, natural rhythm, short sentences.
- React to the SPECIFICS of what they just said before moving on ("Migrating that under load sounds tricky — nice.") — one short, genuine reaction, not generic praise.
- It's okay to use small verbal connectors ("Got it," "Okay, that makes sense," "Interesting").
- Never say "As an AI" or refer to yourself as a bot. If directly asked whether you're an AI, answer honestly in one short sentence and move on.
- After their small-talk answer, respond briefly and warmly, then transition: "Alright, let's get started" and ask them to walk you through their background and one project they're proud of.

INTERVIEW STRUCTURE:
- Ask ONE question at a time. Wait for the full answer.
- 5 questions total: 1 background, 2 behavioral (dig for the STAR shape without naming it), 2 role-specific technical.
- Probe for specifics: numbers, trade-offs, what THEY did vs the team. One follow-up probe max per question.
- Keep your turns SHORT (under 20 seconds of speech). The candidate should do 80% of the talking.
- Never reveal ideal answers. Don't promise outcomes or give a verdict.
${modePrompt ? `\nMODE FOCUS (overrides the default question mix):\n${modePrompt}\n` : ""}
CLOSING:
- After question 5 is answered, close warmly${candidateName ? ` using "${candidateName}"` : ""}: thank them, one genuine positive note about the conversation, then: "That wraps our interview — your score and feedback will be on your Wishire dashboard in a few seconds."
- Always speak clear, neutral English.`,
      },
    ],
  },
  voice: {
    provider: "vapi",
    voiceId: "Naina",
  },
  transcriber: {
    provider: "deepgram",
    model: "nova-2",
    language: "en",
  },
});

// The mic is the one permission the voice interview cannot run without, and
// Vapi's failure payload for a blocked mic carries no message at all — which
// is how a denied prompt used to surface as "Voice connection error". Check it
// ourselves first and say something the student can act on.
const micTroubleMessage = (e) => {
  switch (e?.name) {
    case "NotAllowedError":
    case "SecurityError":
      return "Microphone blocked. Click the padlock in the address bar, set Microphone to Allow, reload, then start again.";
    case "NotFoundError":
    case "OverconstrainedError":
      return "No microphone found. Plug in a mic or headset, then start again.";
    case "NotReadableError":
    case "AbortError":
      return "Your microphone is being used by another app (Zoom, Meet, Teams). Close it, then start again.";
    default:
      return `Microphone unavailable${e?.name ? ` (${e.name})` : ""}. Check the browser's mic permission and try again.`;
  }
};

const preflightMic = async () => {
  if (!window.isSecureContext) {
    return "Browsers only allow microphone access on localhost or over HTTPS. Open this page at localhost (or an https address) and start again.";
  }
  if (!navigator.mediaDevices?.getUserMedia) {
    return "This browser can't reach a microphone. Use Chrome, Edge or Safari.";
  }
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    stream.getTracks().forEach(t => t.stop()); // release it — Vapi opens its own
    return null;
  } catch (e) {
    console.warn("Mic preflight failed:", e);
    return micTroubleMessage(e);
  }
};

// Vapi reports failures in several shapes; dig out whatever is readable and
// keep the raw object in the console rather than swallowing it.
const describeVapiError = (err) => {
  const readable = [
    err?.errorMsg,
    err?.message,
    err?.error?.message,
    err?.error?.msg,
    typeof err?.error === "string" ? err.error : null,
    err?.msg,
    err?.type,
  ].find(v => typeof v === "string" && v.trim());
  if (readable) return readable;
  try {
    const dump = JSON.stringify(err);
    if (dump && dump !== "{}") return `Voice connection failed — ${dump.slice(0, 160)}`;
  } catch (e) {
    /* circular payload — fall through */
  }
  return "Voice connection failed. Check the mic permission, and whether a firewall or VPN is blocking WebRTC.";
};

export default function InterviewPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState("new");
  const [historyList, setHistoryList] = useState([]);
  const [selectedInterview, setSelectedInterview] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);

  useEffect(() => {
    if (tab === "history") {
      setHistoryLoading(true);
      interviewHistory()
        .then(r => setHistoryList(r.data || []))
        .catch(() => setHistoryList([]))
        .finally(() => setHistoryLoading(false));
    }
  }, [tab]);

  const [status, setStatus] = useState("idle"); // idle | connecting | live | ending | scoring | done | error
  const [error, setError] = useState(null);
  const [transcript, setTranscript] = useState([]);
  const [callId, setCallId] = useState(null);
  const [result, setResult] = useState(null);
  const [latest, setLatest] = useState(null);
  const [targetRole, setTargetRole] = useState(user?.target_role || "Software Engineer");
  const [assistantSpeaking, setAssistantSpeaking] = useState(false);
  // Video interviewer (free 3D avatar). Remembered per browser.
  const [avatarOn, setAvatarOn] = useState(() => { try { return localStorage.getItem("wishire.avatar") !== "off"; } catch (_) { return true; } });
  const toggleAvatar = () => setAvatarOn((v) => { try { localStorage.setItem("wishire.avatar", v ? "off" : "on"); } catch (_) {} return !v; });
  const isCampusStudent = !!user?.college_id && user?.role === "candidate";
  const [mode, setMode] = useState(isCampusStudent ? "placement" : "general");
  const [allowance, setAllowance] = useState(null);
  const loadAllowance = () => practiceAllowance().then(({ data }) => setAllowance(data)).catch(() => {});
  useEffect(() => { loadAllowance(); }, []);
  const ivRemaining = allowance?.interviews?.remaining ?? null;

  // ── Proctoring state ─────────────────────────────────────────────────────
  const [cameraActive, setCameraActive] = useState(false);
  const [cameraError, setCameraError] = useState(false);
  const [tabSwitches, setTabSwitches] = useState(0);
  const [tabWarning, setTabWarning] = useState(false);
  const [proctorSummary, setProctorSummary] = useState(null);
  const [faceGate, setFaceGate] = useState(null); // message while we wait for a face before starting
  const webcamRef = useRef(null);
  const streamRef = useRef(null);
  const tabSwitchRef = useRef(0);
  const statusRef = useRef("idle");

  // keep statusRef in sync so visibility handler can read it
  useEffect(() => { statusRef.current = status; }, [status]);

  // On-device face presence: required to start, tracked while live.
  const face = useFaceGuard(webcamRef, cameraActive, status === "live");
  const faceRef = useRef(face); useEffect(() => { faceRef.current = face; }, [face]);
  // Re-attach the stream if the PiP <video> mounts after the camera started.
  useEffect(() => { if (cameraActive && webcamRef.current && streamRef.current && webcamRef.current.srcObject !== streamRef.current) webcamRef.current.srcObject = streamRef.current; }, [cameraActive, status]);

  const startCamera = async () => {
    setCameraError(false);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
      streamRef.current = stream;
      if (webcamRef.current) {
        webcamRef.current.srcObject = stream;
      }
      setCameraActive(true);
    } catch (e) {
      console.warn("Camera access denied:", e);
      setCameraError(true);
      setCameraActive(false);
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    if (webcamRef.current) webcamRef.current.srcObject = null;
    setCameraActive(false);
  };

  // Tab-switch detection
  useEffect(() => {
    const handleVisibility = () => {
      if (document.hidden && statusRef.current === "live") {
        tabSwitchRef.current += 1;
        setTabSwitches(tabSwitchRef.current);
        setTabWarning(true);
        setTimeout(() => setTabWarning(false), 4000);
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () => document.removeEventListener("visibilitychange", handleVisibility);
  }, []);

  // ─────────────────────────────────────────────────────────────────────────

  const vapiRef = useRef(null);
  const startTimeRef = useRef(null);
  const transcriptRef = useRef([]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await latestInterview();
        if (data?.result) setLatest(data);
      } catch (e) {}
    })();
    return () => {
      try { vapiRef.current?.stop(); } catch (e) {}
      stopCamera();
    };
  }, []);

  const appendTurn = (role, text) => {
    if (!text || !text.trim()) return;
    const turn = { role, text: text.trim(), at: new Date().toISOString() };
    transcriptRef.current = [...transcriptRef.current, turn];
    setTranscript([...transcriptRef.current]);
  };

  const stopAndScore = useCallback(async () => {
    stopCamera();
    const switches = tabSwitchRef.current;
    const faceStats = faceRef.current.snapshot();
    setProctorSummary({ tab_switches: switches, camera_was_active: cameraActive || streamRef.current !== null, ...faceStats });
    setStatus("scoring");
    const turns = transcriptRef.current;
    if (turns.length < 2) {
      setError("Interview ended too quickly — try a longer session.");
      setStatus("error");
      return;
    }
    const duration = startTimeRef.current
      ? Math.round((Date.now() - startTimeRef.current) / 1000)
      : null;
    try {
      const { data } = await saveInterview({
        vapi_call_id: callId,
        transcript: turns,
        target_role: targetRole,
        duration_sec: duration,
        tab_switches: switches,
        camera_active: cameraActive || streamRef.current !== null,
        ...faceStats,
      });
      setResult(data.interview.result);
      setLatest(data.interview);
      setStatus("done");
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to score interview.");
      setStatus("error");
    }
  }, [callId, targetRole, cameraActive]);

  const handleStart = async () => {
    setError(null);
    setResult(null);
    setTranscript([]);
    transcriptRef.current = [];
    tabSwitchRef.current = 0;
    setTabSwitches(0);
    setProctorSummary(null);
    setStatus("connecting");

    // Camera + face are required: a proctored interview without a visible face is unverified.
    await startCamera();
    if (!streamRef.current) {
      setError("Turn your camera on to start. The interview is proctored — your face needs to be visible, exactly like a company's online round.");
      setStatus("idle");
      return;
    }
    setFaceGate("Looking for your face… face the camera in good light.");
    const seen = await waitForFace(() => faceRef.current.present, faceRef.current.supported ? 9000 : 1500);
    setFaceGate(null);
    if (!seen && faceRef.current.supported) {
      stopCamera();
      setError("We can't see your face. Sit facing the camera, make sure there's light on your face, and try again.");
      setStatus("idle");
      return;
    }
    faceRef.current.reset();

    // Ask for the mic before handing over to Vapi, so a blocked prompt reads as
    // a blocked prompt instead of a generic connection failure.
    const micProblem = await preflightMic();
    if (micProblem) {
      stopCamera();
      setError(micProblem);
      setStatus("error");
      return;
    }

    try {
      const { data: cfg } = await interviewVapiConfig(mode, targetRole);
      const publicKey = cfg.public_key;
      const role = targetRole || cfg.target_role || "Software Engineer";
      const candidateName = cfg.candidate_name;

      if (!vapiRef.current) {
        vapiRef.current = new Vapi(publicKey);
        const v = vapiRef.current;

        v.on("call-start", () => {
          startTimeRef.current = Date.now();
          setStatus("live");
        });
        v.on("call-end", () => { stopAndScore(); });
        v.on("speech-start", () => setAssistantSpeaking(true));
        v.on("speech-end", () => setAssistantSpeaking(false));
        v.on("message", (msg) => {
          if (msg.type === "transcript" && msg.transcriptType === "final") {
            const role = msg.role === "assistant" || msg.role === "bot" ? "assistant" : "user";
            appendTurn(role, msg.transcript);
          }
        });
        v.on("error", (err) => {
          console.error("VAPI error", err);
          setError(describeVapiError(err));
          setStatus("error");
          stopCamera();
        });
      }

      const assistant = buildAssistant(role, candidateName, cfg.mode_prompt, cfg.max_seconds);
      const call = await vapiRef.current.start(assistant);
      setCallId(call?.id || null);
    } catch (e) {
      console.error(e);
      stopCamera();
      if (e?.response?.status === 402) {
        // Allowance exhausted — refresh so the paywall takes over the panel.
        setStatus("idle");
        loadAllowance();
        return;
      }
      setError(e?.response?.data?.detail || describeVapiError(e));
      setStatus("error");
    }
  };

  const handleStop = async () => {
    setStatus("ending");
    try { vapiRef.current?.stop(); } catch (e) {}
  };

  const restartInterview = () => {
    setStatus("idle");
    setResult(null);
    setTranscript([]);
    transcriptRef.current = [];
    tabSwitchRef.current = 0;
    setTabSwitches(0);
    setProctorSummary(null);
    if (vapiRef.current) vapiRef.current = null;
  };

  const live = status === "live";
  const showResult = status === "done" || (result && status !== "live");

  return (
    <div className="container-x py-10 md:py-14">
      <div className="mb-10">
        <span className="overline">Voice mock interview</span>
        <h1 className="font-display text-4xl md:text-5xl mt-3 leading-tight">
          Practice with{" "}
          <span className="italic text-stone-500">a real AI interviewer.</span>
        </h1>
        <p className="mt-4 text-stone-600 max-w-2xl">
          5 questions · about {allowance?.session_minutes || 6} minutes · transcript + score generated automatically.
        </p>
      </div>

      {/* Tab-switch warning banner */}
      {tabWarning && (
        <div className="mb-4 flex items-center gap-3 px-5 py-3 rounded-2xl bg-red-50 border border-red-200 text-red-700 text-sm font-medium animate-pulse">
          <ShieldAlert size={16} className="shrink-0" />
          Tab switch detected ({tabSwitches}). Proctoring is active — stay on this page.
        </div>
      )}

      {/* Tab switcher */}
      <div className="flex gap-2 mb-8">
        <button
          onClick={() => setTab("new")}
          className={`px-5 py-2.5 rounded-full text-sm font-medium transition-colors ${
            tab === "new" ? "bg-stone-900 text-stone-50" : "bg-stone-100 text-stone-600 hover:bg-stone-200"
          }`}
        >
          New Interview
        </button>
        <button
          onClick={() => setTab("history")}
          className={`px-5 py-2.5 rounded-full text-sm font-medium transition-colors inline-flex items-center gap-2 ${
            tab === "history" ? "bg-stone-900 text-stone-50" : "bg-stone-100 text-stone-600 hover:bg-stone-200"
          }`}
        >
          <Clock size={14} /> History
        </button>
      </div>

      {tab === "history" ? (
        <HistoryTab
          loading={historyLoading}
          list={historyList}
          selected={selectedInterview}
          onSelect={setSelectedInterview}
        />
      ) : showResult ? (
        <InterviewResults
          result={result || latest?.result}
          proctorSummary={proctorSummary}
          onRestart={restartInterview}
        />
      ) : ivRemaining === 0 && status === "idle" ? (
        <PaywallCard allowance={allowance} onPurchased={loadAllowance} />
      ) : (
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        {/* Control panel */}
        <div className="lg:col-span-5 min-w-0 rounded-3xl bg-stone-900 text-stone-50 p-8 relative overflow-hidden wsh-grain">
          <div
            className="absolute inset-0 opacity-30"
            style={{
              background:
                "radial-gradient(500px 300px at 80% 10%, rgba(217,119,6,0.4), transparent 60%)",
            }}
          />
          <div className="relative">
            <span className="overline" style={{ color: "rgba(253,251,247,0.55)" }}>
              Session
              {ivRemaining != null && (
                <span className="ml-2 normal-case tracking-normal font-sans text-[11px] px-2 py-0.5 rounded-full bg-amber-300/15 border border-amber-300/30 text-amber-300" data-testid="iv-remaining">
                  {ivRemaining} of {allowance?.interviews?.total ?? ivRemaining} free session{(allowance?.interviews?.total ?? ivRemaining) !== 1 ? "s" : ""} left · {allowance?.session_minutes || 6} min each
                </span>
              )}
            </span>
            {allowance?.pack && status === "idle" && (
              <p className="mt-2 text-[11px] text-stone-400" data-testid="iv-allowance-note">
                {ivRemaining > 0
                  ? `Free sessions come with your college licence. When they're used, the Practice Pack adds ${allowance.pack.interviews} interviews + ${allowance.pack.mock_tests} proctored tests (${allowance.pack.session_minutes}-min sessions) for ₹${allowance.pack.price_inr}.`
                  : `Your free sessions are used. The Practice Pack adds ${allowance.pack.interviews} interviews + ${allowance.pack.mock_tests} proctored tests for ₹${allowance.pack.price_inr}.`}
              </p>
            )}
            <h2 className="font-display text-3xl mt-2 mb-6">
              {status === "idle" && "Ready when you are"}
              {status === "connecting" && "Connecting…"}
              {status === "live" && "You're live"}
              {status === "ending" && "Wrapping up…"}
              {status === "scoring" && "Scoring your answers…"}
              {status === "done" && "Interview complete"}
              {status === "error" && "Something went wrong"}
            </h2>

            {/* Video interviewer */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <p className="overline" style={{ color: "rgba(253,251,247,0.55)" }}>Your interviewer</p>
                <button type="button" onClick={toggleAvatar} data-testid="avatar-toggle"
                  className={`text-[11px] px-2.5 py-1 rounded-full border ${avatarOn ? "bg-amber-300/15 border-amber-300/30 text-amber-300" : "bg-white/10 border-white/20 text-stone-300"}`}>
                  {avatarOn ? "Video on" : "Voice only"}
                </button>
              </div>
              {avatarOn ? (
                <AvatarInterviewer vapiRef={vapiRef} status={status} speaking={assistantSpeaking} mood={mode === "hr" ? "happy" : "neutral"} className="w-full aspect-[4/3]" />
              ) : (
                <p className="text-xs text-stone-400">Voice-only mode. Turn video on to practise eye contact with a live interviewer.</p>
              )}
            </div>

            {faceGate && <p className="mb-3 text-xs text-amber-300" data-testid="face-gate">{faceGate}</p>}
            {/* Proctoring status pill */}
            {(live || status === "connecting") && (
              <div className="mb-4 flex items-center gap-2 flex-wrap">
                <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${
                  cameraActive ? "bg-emerald-500/20 text-emerald-300 border border-emerald-500/30" : "bg-red-500/20 text-red-300 border border-red-500/30"
                }`}>
                  {cameraActive ? <Camera size={11}/> : <CameraOff size={11}/>}
                  {cameraActive ? "Camera on" : cameraError ? "Camera denied" : "Camera off"}
                </span>
                <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${
                  tabSwitches === 0 ? "bg-emerald-500/20 text-emerald-300 border border-emerald-500/30" : "bg-red-500/20 text-red-300 border border-red-500/30"
                }`}>
                  <ShieldCheck size={11}/>
                  {tabSwitches === 0 ? "No tab switches" : `${tabSwitches} tab switch${tabSwitches > 1 ? "es" : ""}`}
                </span>
              </div>
            )}

            {/* Mode selector */}
            <div className="mb-4">
              <p className="overline mb-2" style={{color:"rgba(253,251,247,0.55)"}}>Interview type</p>
              <div className="flex flex-wrap gap-2">
                {[
                  ...(isCampusStudent ? [{ id: "placement", label: "🎓 Campus Placement" }] : []),
                  { id: "general", label: "General" },
                  { id: "behavioural", label: "Behavioural" },
                  { id: "technical", label: "Technical" },
                  { id: "hr", label: "HR Round" },
                  { id: "case", label: "Case Study" },
                ].map(m => (
                  <button
                    key={m.id}
                    type="button"
                    disabled={live}
                    onClick={() => setMode(m.id)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                      mode === m.id
                        ? "bg-amber-300 text-stone-900"
                        : "bg-white/10 border border-white/20 text-stone-300 hover:bg-white/20"
                    } disabled:opacity-50`}
                  >
                    {m.label}
                  </button>
                ))}
              </div>
            </div>

            <input
              type="text"
              data-testid="interview-role-input"
              placeholder="Target role"
              value={targetRole}
              onChange={(e) => setTargetRole(e.target.value)}
              disabled={live}
              className="w-full mb-4 px-4 py-2.5 rounded-full bg-white/10 border border-white/20 text-stone-50 placeholder:text-stone-500 focus:outline-none focus:border-amber-300 text-sm"
            />

            <div className={`flex justify-center my-8 ${avatarOn ? "hidden" : ""}`}>
              <div className={`w-32 h-32 rounded-full flex items-center justify-center transition-all ${
                live
                  ? "bg-[#C2410C] wsh-pulse"
                  : status === "scoring"
                    ? "bg-amber-500"
                    : "bg-white/10 border border-white/20"
              }`}>
                {status === "scoring" ? (
                  <Loader2 className="animate-spin" size={36} />
                ) : live ? (
                  <Mic size={36} />
                ) : (
                  <MicOff size={36} />
                )}
              </div>
            </div>

            {live && (
              <div className="mb-6">
                <div className="flex items-center justify-center gap-3 mb-4">
                  <div className={`w-10 h-10 rounded-full bg-gradient-to-br from-amber-300 to-amber-500 text-stone-900 font-display flex items-center justify-center transition-transform ${assistantSpeaking ? "scale-110" : ""}`}>
                    {INTERVIEWER_NAME[0]}
                  </div>
                  <div className="text-left">
                    <p className="text-sm font-medium text-stone-50">{INTERVIEWER_NAME}</p>
                    <p className="text-[10px] font-mono uppercase tracking-widest" style={{ color: "rgba(253,251,247,0.55)" }}>Hiring manager · Wishire</p>
                  </div>
                </div>
                <Waveform active={assistantSpeaking} color="#FCD34D" />
                <p className="overline text-center mt-3" style={{ color: "rgba(253,251,247,0.55)" }}>
                  {assistantSpeaking ? `${INTERVIEWER_NAME} is speaking` : "Your turn — take your time"}
                </p>
              </div>
            )}

            <div className="space-y-3">
              {!live && status !== "scoring" && status !== "connecting" && (
                <button
                  type="button"
                  data-testid="interview-start"
                  onClick={handleStart}
                  className="w-full inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-full bg-amber-300 text-stone-900 hover:bg-amber-200 transition-colors font-medium"
                >
                  <Mic size={16} /> Start interview
                </button>
              )}
              {(status === "connecting" || status === "ending") && (
                <button
                  disabled
                  className="w-full inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-full bg-white/10 border border-white/20 text-stone-300 font-medium"
                >
                  <Loader2 size={16} className="animate-spin" /> Please wait…
                </button>
              )}
              {live && (
                <button
                  type="button"
                  data-testid="interview-stop"
                  onClick={handleStop}
                  className="w-full inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-full border border-white/30 text-stone-50 hover:bg-white/10 transition-colors font-medium"
                >
                  <MicOff size={16} /> End interview
                </button>
              )}
              {status === "done" && (
                <button
                  type="button"
                  data-testid="interview-restart"
                  onClick={() => {
                    setStatus("idle");
                    setResult(null);
                    setTranscript([]);
                    transcriptRef.current = [];
                    tabSwitchRef.current = 0;
                    setTabSwitches(0);
                    setProctorSummary(null);
                    if (vapiRef.current) { vapiRef.current = null; }
                  }}
                  className="w-full inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-full bg-amber-300 text-stone-900 hover:bg-amber-200 transition-colors font-medium"
                >
                  <RotateCcw size={16} /> Run another
                </button>
              )}
            </div>

            {error && (
              <p data-testid="interview-error" className="mt-4 text-amber-200 text-sm inline-flex items-center gap-2">
                <AlertTriangle size={14} /> {error}
              </p>
            )}

            {/* Camera info when idle */}
            {status === "idle" && (
              <p className="mt-5 text-xs text-stone-500 flex items-center gap-1.5">
                <Camera size={11}/> Camera + tab proctoring activates when you start.
              </p>
            )}
          </div>
        </div>

        {/* Right panel: live transcript */}
        <div className="lg:col-span-7 min-w-0 rounded-3xl bg-white border border-stone-200 p-7 min-h-[420px]">
          <TranscriptView transcript={transcript} live={live} />
        </div>
      </div>
      )}

      {/* ── Webcam PiP overlay ─────────────────────────────────────────── */}
      {(live || status === "connecting" || status === "ending") && (
        <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end gap-2">
          {/* Camera feed */}
          <div className="relative rounded-2xl overflow-hidden shadow-2xl border-2 border-stone-700 bg-stone-900 w-44 h-32">
            <video
              ref={webcamRef}
              autoPlay
              muted
              playsInline
              className={`w-full h-full object-cover ${cameraActive ? "opacity-100" : "opacity-0"}`}
            />
            {!cameraActive && (
              <div className="absolute inset-0 flex flex-col items-center justify-center text-stone-500 text-xs gap-1">
                <CameraOff size={20} />
                <span>{cameraError ? "Camera denied" : "No camera"}</span>
              </div>
            )}
            {cameraActive && face.ready && !face.present && status === "live" && (
              <div className="absolute inset-0 bg-red-600/80 flex flex-col items-center justify-center text-white text-[11px] font-medium text-center px-2" data-testid="face-missing">
                We can't see your face — face the camera
              </div>
            )}
            {cameraActive && face.present && face.faces > 1 && status === "live" && (
              <div className="absolute inset-x-0 bottom-0 bg-amber-500/90 text-stone-900 text-[10px] font-medium text-center py-0.5">{face.faces} faces in frame</div>
            )}
            {/* Live indicator */}
            <div className="absolute top-2 left-2 flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-red-500 wsh-pulse" />
              <span className="text-[9px] text-white font-mono uppercase tracking-widest">Live</span>
            </div>
            {/* Tab switch count */}
            {tabSwitches > 0 && (
              <div className="absolute top-2 right-2 bg-red-500 text-white text-[9px] font-mono rounded-full px-1.5 py-0.5">
                {tabSwitches} switch{tabSwitches > 1 ? "es" : ""}
              </div>
            )}
            {/* You label */}
            <div className="absolute bottom-2 left-2 text-[9px] text-white/70 font-mono">You</div>
          </div>

          {/* Proctoring label */}
          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-stone-900/90 backdrop-blur text-[10px] text-stone-300 border border-stone-700">
            <ShieldCheck size={10} className="text-emerald-400" />
            Proctored session
          </div>
        </div>
      )}
    </div>
  );
}

function HistoryTab({ loading, list, selected, onSelect }) {
  if (loading) return <div className="flex items-center gap-3 text-stone-500"><Loader2 className="animate-spin" size={18}/> Loading history…</div>;
  if (list.length === 0) return <div className="rounded-3xl border-2 border-dashed border-stone-200 py-20 text-center text-stone-400">No past interviews yet. Complete one to see it here.</div>;

  if (selected) {
    const r = selected.result || {};
    return (
      <div>
        <button onClick={() => onSelect(null)} className="inline-flex items-center gap-1 text-sm text-stone-500 hover:text-stone-900 mb-6">
          ← Back to history
        </button>
        <div className="space-y-6">
          <div className="rounded-3xl bg-white border border-stone-200 p-7">
            <ResultView result={r} />
          </div>
          <div className="rounded-3xl bg-white border border-stone-200 p-7">
            <p className="overline mb-4">Transcript</p>
            <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-2">
              {(selected.transcript || []).map((t, i) => (
                <div key={i} className={`rounded-2xl p-4 leading-relaxed ${t.role === "assistant" ? "bg-amber-50 border border-amber-100" : "bg-stone-100"}`}>
                  <p className="overline mb-1">{t.role === "assistant" ? `${INTERVIEWER_NAME} · Interviewer` : "You"}</p>
                  <p className="text-stone-900">{t.text}</p>
                </div>
              ))}
              {(!selected.transcript || selected.transcript.length === 0) && <p className="text-stone-400 text-sm">No transcript available.</p>}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {list.map((iv, i) => {
        const r = iv.result || {};
        return (
          <button key={iv._id || i} onClick={() => onSelect(iv)}
            className="w-full rounded-2xl bg-white border border-stone-200 p-5 flex items-center justify-between hover:shadow-md transition-shadow text-left gap-4">
            <div>
              <p className="font-display text-lg">{iv.target_role || "Interview"}</p>
              <p className="text-xs text-stone-400 font-mono mt-0.5">
                {iv.created_at ? new Date(iv.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : ""}
              </p>
            </div>
            <div className="flex items-center gap-4 shrink-0">
              {r.overall_score != null && (
                <div className="text-center">
                  <p className="font-display text-2xl">{r.overall_score}</p>
                  <p className="text-xs text-stone-400">score</p>
                </div>
              )}
              {r.verdict && (
                <span className="px-3 py-1 rounded-full bg-amber-50 border border-amber-100 text-amber-800 text-xs font-medium">{r.verdict}</span>
              )}
              <ChevronRight size={16} className="text-stone-400"/>
            </div>
          </button>
        );
      })}
    </div>
  );
}

function TranscriptView({ transcript, live }) {
  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <span className="overline">Live transcript</span>
        {live && (
          <span className="font-mono text-xs text-emerald-700 inline-flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-600 wsh-pulse" /> recording
          </span>
        )}
      </div>
      <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2" data-testid="interview-transcript">
        {transcript.length === 0 ? (
          <p className="text-stone-500">
            Click <strong>Start interview</strong> and grant mic + camera access. Your live
            transcript will appear here.
          </p>
        ) : (
          transcript.map((t, i) => (
            <div
              key={i}
              className={`rounded-2xl p-4 leading-relaxed ${
                t.role === "assistant"
                  ? "bg-[#FEF3C7] border border-amber-100"
                  : "bg-stone-100"
              }`}
            >
              <p className="overline mb-1">
                {t.role === "assistant" ? `${INTERVIEWER_NAME} · Interviewer` : "You"}
              </p>
              <p className="text-stone-900">{t.text}</p>
            </div>
          ))
        )}
      </div>
    </>
  );
}

function InterviewResults({ result, proctorSummary, onRestart }) {
  if (!result) {
    return (
      <div className="rounded-3xl border border-stone-200 bg-white p-10 text-center text-stone-500">
        No score available. <button onClick={onRestart} className="text-[#C2410C] underline ml-1">Run another interview</button>
      </div>
    );
  }
  const clean = proctorSummary ? proctorSummary.tab_switches === 0 : true;
  const dims = [
    { label: "Clarity", value: result.clarity },
    { label: "Structure", value: result.structure },
    { label: "Depth", value: result.depth },
    { label: "Communication", value: result.communication },
  ];

  return (
    <div className="space-y-8 max-w-5xl">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <span className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-widest text-[#15803D] bg-emerald-50 px-3 py-1 rounded-full">
            <CheckCircle2 size={12} /> Interview complete
          </span>
          <h2 className="font-display text-4xl md:text-5xl mt-3 leading-tight">
            Here&apos;s how you <span className="vib-text">did.</span>
          </h2>
        </div>
        <button
          onClick={onRestart}
          data-testid="interview-restart"
          className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-stone-900 text-white text-sm font-medium hover:bg-[#C2410C] transition-colors"
        >
          <RotateCcw size={15} /> Run another
        </button>
      </div>

      {result.counted === false && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 px-5 py-3 text-sm text-amber-800" data-testid="interview-uncounted">
          <b>Not counted toward your Wishire Score.</b> {result.counted_reason}
        </div>
      )}
      {/* Score hero — full width */}
      <div className="rounded-[2rem] vib-hero text-stone-50 p-8 md:p-10 overflow-hidden wsh-grain">
        <div className="grid grid-cols-1 md:grid-cols-[auto_1fr] gap-8 md:gap-10 items-center">
          <div className="flex justify-center"><div className="rounded-full vib-ring-glow"><ScoreRing value={result.overall_score} size={200} dark /></div></div>
          <div className="min-w-0">
            <span className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest px-3 py-1 rounded-full bg-amber-400/20 text-amber-300">
              Verdict · {result.verdict}
            </span>
            <p className="mt-4 text-stone-200 leading-relaxed" data-testid="interview-summary">{result.summary}</p>
            <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
              {dims.map((d) => (
                <div key={d.label} className="rounded-2xl bg-white/10 border border-white/10 px-4 py-3">
                  <p className="font-display text-3xl">{d.value ?? 0}</p>
                  <p className="text-[10px] font-mono uppercase tracking-widest text-stone-400 mt-0.5">{d.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Communication gap analysis */}
      {result.communication_breakdown && Object.keys(result.communication_breakdown).length > 0 && (
        <div className="rounded-3xl border border-stone-200 bg-white p-7" data-testid="comm-breakdown">
          <h3 className="font-display text-2xl">How you spoke</h3>
          <p className="text-sm text-stone-500 mt-1">Your communication score blends this analysis with hard metrics from your actual answers — it directly feeds your Wishire Score.</p>
          <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[["Fluency", result.communication_breakdown.fluency], ["Vocabulary", result.communication_breakdown.vocabulary],
              ["Confidence", result.communication_breakdown.confidence], ["Answer quality", result.communication_breakdown.answer_quality]].map(([l, v]) => (
              <div key={l} className="rounded-2xl bg-stone-50 border border-stone-100 px-4 py-3">
                <p className={`font-display text-3xl ${v >= 70 ? "text-emerald-600" : v >= 45 ? "text-amber-600" : "text-red-600"}`}>{v ?? "—"}</p>
                <p className="text-[10px] font-mono uppercase tracking-widest text-stone-400 mt-0.5">{l}</p>
              </div>
            ))}
          </div>
          {result.speech_metrics && (
            <div className="mt-4 flex flex-wrap gap-2 text-xs">
              <span className="px-3 py-1.5 rounded-full bg-stone-100 text-stone-600">{result.speech_metrics.answers} answers · avg {result.speech_metrics.avg_words} words</span>
              <span className={`px-3 py-1.5 rounded-full ${result.speech_metrics.filler_rate > 4 ? "bg-red-50 text-red-700 border border-red-200" : "bg-emerald-50 text-emerald-700"}`}>
                {result.speech_metrics.filler_rate} filler words per 100{(result.speech_metrics.top_fillers || []).length > 0 ? ` — mostly "${result.speech_metrics.top_fillers.join('", "')}"` : ""}
              </span>
              {result.speech_metrics.short_answers > 0 && (
                <span className="px-3 py-1.5 rounded-full bg-amber-50 text-amber-700">{result.speech_metrics.short_answers} one-liner answer{result.speech_metrics.short_answers !== 1 ? "s" : ""}</span>
              )}
            </div>
          )}
          {(result.communication_breakdown.weak_moments || []).length > 0 && (
            <div className="mt-4 space-y-2">
              {result.communication_breakdown.weak_moments.map((w, i) => (
                <div key={i} className="rounded-xl bg-amber-50 border border-amber-200 px-4 py-2.5 text-sm">
                  <p className="italic text-stone-700">"{w.quote}"</p>
                  <p className="text-xs text-amber-800 mt-1">{w.issue}</p>
                </div>
              ))}
            </div>
          )}
          {(result.communication_breakdown.tips || []).length > 0 && (
            <ul className="mt-4 space-y-1.5">
              {result.communication_breakdown.tips.map((t, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-stone-700"><CheckCircle2 size={14} className="text-[#C2410C] mt-0.5 shrink-0" /> {t}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Proctoring report — verified true/false, plus why */}
      {proctorSummary && (() => {
        const pr = result?.proctor;
        const verified = pr ? pr.verified : clean;
        return (
          <div className={`flex items-center gap-4 p-5 rounded-2xl border ${verified ? "bg-emerald-50 border-emerald-200" : "bg-red-50 border-red-200"}`} data-testid="proctor-verdict">
            {verified ? <ShieldCheck size={20} className="text-emerald-600 shrink-0" /> : <ShieldAlert size={20} className="text-red-600 shrink-0" />}
            <div className="min-w-0">
              <p className={`text-sm font-medium ${verified ? "text-emerald-800" : "text-red-800"}`}>Proctoring report · Verified: {verified ? "true" : "false"}{pr && ` · ${pr.face_visible ? "face visible" : "face not visible"}`}</p>
              <p className="text-xs mt-0.5 text-stone-600">
                {pr?.notes?.length ? pr.notes.join(" · ") : <>{proctorSummary.camera_was_active ? "✓ Camera active throughout" : "⚠ Camera was not active"} · {proctorSummary.tab_switches ? `⚠ ${proctorSummary.tab_switches} tab switch${proctorSummary.tab_switches > 1 ? "es" : ""}` : "✓ No tab switches"}{proctorSummary.face_checks > 0 && <> · {proctorSummary.face_absent_sec <= 2 ? "✓ Face visible throughout" : `⚠ Face away for ${Math.round(proctorSummary.face_absent_sec)}s`}</>}</>}
              </p>
              {!verified && <p className="text-xs mt-1 text-stone-500">This interview is marked unverified and counts for less in your Wishire Score. Keep your face in the camera next time.</p>}
            </div>
          </div>
        );
      })()}

      {/* Highlights / Improvements */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="rounded-3xl border border-emerald-200 bg-emerald-50/50 p-6 min-w-0">
          <Block title="What went well" items={result.highlights} accent="#15803D" />
        </div>
        <div className="rounded-3xl border border-[#C2410C]/20 bg-[#fff7ed] p-6 min-w-0">
          <Block title="Where to improve" items={result.improvements} accent="#C2410C" />
        </div>
      </div>

      {/* Coaching plan — full width */}
      <div>
        <div className="mb-5">
          <span className="overline">What to do next</span>
          <h3 className="font-display text-3xl mt-2">Back to <span className="vib-text">My Plan.</span></h3>
          <p className="text-stone-600 mt-1">Your next steps toward placement day — tick them off as you go.</p>
        </div>
        <CoachingPlan autoLoad />
      </div>
    </div>
  );
}

function ResultView({ result, proctorSummary }) {
  if (!result) return <p className="text-stone-500">No score yet.</p>;
  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <span className="overline">Interview score</span>
        <span className="inline-flex items-center gap-2 text-emerald-700 text-sm">
          <CheckCircle2 size={14} /> AI-graded
        </span>
      </div>
      <div className="flex flex-col md:flex-row gap-8 items-center">
        <ScoreRing value={result.overall_score} size={200} />
        <div className="flex-1 min-w-0">
          <p className="font-display text-2xl">
            Verdict: <span className="text-[#C2410C]">{result.verdict}</span>
          </p>
          <p className="mt-3 text-stone-700 leading-relaxed" data-testid="interview-summary">
            {result.summary}
          </p>
          <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label="Clarity" value={result.clarity} />
            <Stat label="Structure" value={result.structure} />
            <Stat label="Depth" value={result.depth} />
            <Stat label="Comms" value={result.communication} />
          </div>
        </div>
      </div>

      {/* Proctoring summary */}
      {proctorSummary && (
        <div className={`mt-6 flex items-center gap-4 p-4 rounded-2xl border ${
          proctorSummary.tab_switches === 0
            ? "bg-emerald-50 border-emerald-200"
            : "bg-amber-50 border-amber-200"
        }`}>
          {proctorSummary.tab_switches === 0
            ? <ShieldCheck size={18} className="text-emerald-600 shrink-0"/>
            : <ShieldAlert size={18} className="text-amber-600 shrink-0"/>
          }
          <div>
            <p className={`text-sm font-medium ${proctorSummary.tab_switches === 0 ? "text-emerald-800" : "text-amber-800"}`}>
              Proctoring report
            </p>
            <p className="text-xs mt-0.5 text-stone-600">
              {proctorSummary.camera_was_active ? "✓ Camera was active throughout" : "⚠ Camera was not active"} &nbsp;·&nbsp;
              {proctorSummary.tab_switches === 0 ? "✓ No tab switches detected" : `⚠ ${proctorSummary.tab_switches} tab switch${proctorSummary.tab_switches > 1 ? "es" : ""} detected`}
            </p>
          </div>
        </div>
      )}

      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
        <Block title="Highlights" items={result.highlights} accent="#15803D" />
        <Block title="Improvements" items={result.improvements} accent="#C2410C" />
      </div>

      {/* Coaching plan — auto-loads after interview scored */}
      <div className="mt-10">
        <div className="mb-5">
          <span className="overline">What to do next</span>
          <h2 className="font-display text-3xl mt-2">Back to My Plan.</h2>
          <p className="text-stone-600 mt-1">
            Your next steps toward placement day — tick them off as you go.
          </p>
        </div>
        <CoachingPlan autoLoad />
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-xl bg-stone-100 px-3 py-2">
      <p className="overline">{label}</p>
      <p className="font-display text-2xl mt-1">{value ?? 0}</p>
    </div>
  );
}

function Block({ title, items, accent }) {
  return (
    <div>
      <p className="overline mb-3" style={{ color: accent }}>
        {title}
      </p>
      <ul className="space-y-2">
        {(items || []).map((s, i) => (
          <li key={i} className="text-stone-800 leading-relaxed flex gap-2">
            <span style={{ color: accent }}>•</span>
            <span>{s}</span>
          </li>
        ))}
        {(!items || items.length === 0) && (
          <li className="text-stone-500 text-sm">—</li>
        )}
      </ul>
    </div>
  );
}
